import { useEffect, useState } from "react";
import { User, Phone, Mail, MapPin, Briefcase, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { Spinner } from "@/components/common/Loading/Spinner";
import { fetchContactDetails } from "@/api/contact/api";
import { useLayout } from "@/contexts/LayoutContext";
import { ContactDetails } from "@/types/domain";

type ContactDetailsModalProps = {
  contactId: string;
  contactName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DetailLine = ({ value }: { value: string }) => {
  if (!value) return null;
  return <span className="block">{value}</span>;
};

export const ContactDetailsModal = ({ contactId, contactName, open, onOpenChange }: ContactDetailsModalProps) => {
  const { data } = useLayout();
  const [details, setDetails] = useState<ContactDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !contactId) return;

    setIsLoading(true);
    setError(null);
    setDetails(null);

    fetchContactDetails(contactId)
      .then(setDetails)
      .catch(() => setError("Failed to load contact details."))
      .finally(() => setIsLoading(false));
  }, [open, contactId]);

  // Filter out empty strings — all fields are guaranteed strings from the API, but may be ""
  const addressLines = details
    ? [details.address1, details.address2, details.address3, details.address4, details.town, details.county, details.postcode, details.country].filter(Boolean)
    : [];

  const companyUrl = details ? `${data.appRoutes.dashboard}?customerid=${details.companyId}` : null;
  const officeTelephone = details ? details.workTelephone || details.companyTelephone : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User size={16} className="shrink-0 text-muted-foreground" />
            {details ? details.contactName : contactName}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive py-2">{error}</p>
        )}

        {details && !isLoading && (
          <div className="space-y-3 text-sm">
            {(details.jobDescription || details.department) && (
              <div className="flex gap-2">
                <Briefcase size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div className="leading-snug">
                  <DetailLine value={details.jobDescription} />
                  <DetailLine value={details.department} />
                </div>
              </div>
            )}

            {companyUrl && (
              <div className="flex gap-2 items-center">
                <Building2 size={14} className="shrink-0 text-muted-foreground" />
                <a href={companyUrl} className="hover:underline text-blue-600 text-sm">
                  {details.accountNo} — {details.companyName}
                </a>
              </div>
            )}

            {addressLines.length > 0 && (
              <div className="flex gap-2">
                <MapPin size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                <address className="not-italic leading-snug">
                  {addressLines.map((line, i) => (
                    <DetailLine key={i} value={line} />
                  ))}
                </address>
              </div>
            )}

            {details.email && (
              <div className="flex gap-2 items-center">
                <Mail size={14} className="shrink-0 text-muted-foreground" />
                <a href={`mailto:${details.email}`} className="hover:underline truncate">
                  {details.email}
                </a>
              </div>
            )}

            {officeTelephone && (
              <div className="flex gap-2 items-center">
                <Phone size={14} className="shrink-0 text-muted-foreground" />
                <a href={`tel:${officeTelephone}`} className="hover:underline">
                  {officeTelephone}
                </a>
              </div>
            )}

            {details.mobileTelephone && (
              <div className="flex gap-2 items-center">
                <Phone size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground text-xs mr-1">M</span>
                <a href={`tel:${details.mobileTelephone}`} className="hover:underline">
                  {details.mobileTelephone}
                </a>
              </div>
            )}

            {details.homeTelephone && (
              <div className="flex gap-2 items-center">
                <Phone size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground text-xs mr-1">H</span>
                <a href={`tel:${details.homeTelephone}`} className="hover:underline">
                  {details.homeTelephone}
                </a>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
